import { useCallback, useEffect, useState } from 'react';

import { chatApi } from '../api';
import { useRuntimeSettings } from '../context/RuntimeSettingsContext';
import { useToast } from '../context/ToastContext';

/** The sidebar's conversation list. */
export function useConversations() {
  const toast = useToast();
  // Settings → General, "Conversations listed in the sidebar".
  const limit = useRuntimeSettings().effective.conversation_list_size || 60;
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const list = await chatApi.listConversations({ limit });
      setConversations(list);
      return list;
    } catch (cause) {
      toast.error(cause, { title: 'Could not load conversations' });
      return [];
    } finally {
      setLoading(false);
    }
  }, [limit, toast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const remove = useCallback(
    async (id) => {
      try {
        await chatApi.remove(id);
        setConversations((current) => current.filter((conversation) => conversation.id !== id));
      } catch (cause) {
        toast.error(cause, { title: 'Could not delete that conversation' });
      }
    },
    [toast],
  );

  const setPinned = useCallback(
    async (id, pinned) => {
      try {
        const updated = await chatApi.setPinned(id, pinned);
        // Re-fetch rather than patch in place: pinning changes the *order*, and the
        // server owns that ordering (pinned first, then recency). Sorting again here
        // would be a second implementation of the same rule, free to disagree.
        setConversations((current) =>
          current.map((conversation) => (conversation.id === id ? updated : conversation)),
        );
        await refresh();
      } catch (cause) {
        toast.error(cause, {
          title: pinned ? 'Could not pin that chat' : 'Could not unpin that chat',
        });
      }
    },
    [refresh, toast],
  );

  const rename = useCallback(
    async (id, title) => {
      try {
        const updated = await chatApi.rename(id, title);
        setConversations((current) =>
          current.map((conversation) => (conversation.id === id ? updated : conversation)),
        );
      } catch (cause) {
        toast.error(cause, { title: 'Could not rename that conversation' });
      }
    },
    [toast],
  );

  return { conversations, loading, refresh, remove, rename, setPinned };
}
